/**
 * Copied caller words: the deterministic text check on every finding text (plan-032 DOM-OR, design §43.6.2).
 *
 * A finding is stored outside transcript protection (`optimisation_finding` is read with `agent:read`, swept by no
 * retention class and touched by no erasure), so no caller's words may reach it (design §43.15, OD-4). The model is
 * told so; this check is what makes it true. Applied to `issue`, the one-line `evidence` description, every `summary`,
 * and an `answer` fix's `question` and `answer`. The refusals, in the order they are tested:
 *
 *   too_long            longer than the field allows (issue 240, summary 300, question 300, answer 2,000)
 *   copied_caller_words a run of four or more consecutive words that occurs in a caller turn of the read set, after
 *                       folding case, accents (Greek tonos and dialytika included), final sigma and punctuation
 *   digit_run           five or more digits in a row (a single space or hyphen between digits is still a run: a phone
 *                       number written "69 1234 5678" is one run of ten)
 *   email_address       an email address
 *   web_address         a web address (`https://…`, `www.…`, or a bare `name.tld`)
 *
 * The last three are allowed when the matched text occurs verbatim in the reviewed version's own `config_document`
 * (the business's own number or address is not a stranger's). Pure; the answer names a code, never the offending text.
 */
import { OPTIMISATION_FINDING_ISSUE_MAXIMUM, OPTIMISATION_FIX_ANSWER_MAXIMUM, OPTIMISATION_FIX_QUESTION_MAXIMUM, OPTIMISATION_FIX_SUMMARY_MAXIMUM, } from "./constant.js";
export const OPTIMISATION_FINDING_TEXT_PROBLEM = [
    "too_long",
    "copied_caller_words",
    "digit_run",
    "email_address",
    "web_address",
];
const MAXIMUM = Object.freeze({
    issue: OPTIMISATION_FINDING_ISSUE_MAXIMUM,
    evidence: OPTIMISATION_FINDING_ISSUE_MAXIMUM,
    summary: OPTIMISATION_FIX_SUMMARY_MAXIMUM,
    question: OPTIMISATION_FIX_QUESTION_MAXIMUM,
    answer: OPTIMISATION_FIX_ANSWER_MAXIMUM,
});
/** How many consecutive folded words make a copy (design §43.6.2). */
export const COPIED_CALLER_WORD_RUN = 4;
/**
 * Folds a text into comparable words: NFD, combining marks dropped (Greek tonos ´ and dialytika ¨, Latin accents),
 * lower case, final sigma read as sigma, and every run of anything that is not a letter or a digit read as one break.
 */
export function foldedWordsOf(text) {
    return text
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .replace(/ς/gu, "σ")
        .split(/[^\p{L}\p{N}]+/u)
        .filter((word) => word !== "");
}
/** The caller turns of a read set, folded once into the set of their four-word runs. */
export class CopiedCallerWordIndex {
    #run;
    constructor(callerTurns) {
        const run = new Set();
        for (const turn of callerTurns) {
            for (const one of wordRunsOf(foldedWordsOf(turn)))
                run.add(one);
        }
        this.#run = run;
    }
    /** Whether `text` carries a run of {@link COPIED_CALLER_WORD_RUN} words that a caller said. */
    copiedIn(text) {
        for (const one of wordRunsOf(foldedWordsOf(text))) {
            if (this.#run.has(one))
                return true;
        }
        return false;
    }
}
function wordRunsOf(words) {
    const out = [];
    for (let at = 0; at + COPIED_CALLER_WORD_RUN <= words.length; at += 1) {
        out.push(words.slice(at, at + COPIED_CALLER_WORD_RUN).join(" "));
    }
    return out;
}
/** Five or more digits, a single space or hyphen allowed between two digits. */
const DIGIT_RUN = /\d(?:[ -]?\d){4,}/gu;
const EMAIL_ADDRESS = /[\p{L}\p{N}._%+-]+@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+/gu;
/** `scheme://…`, `www.…`, or a bare `label(.label)*.tld` with an alphabetic top-level label of two letters or more. */
const WEB_ADDRESS = /(?:[a-z][a-z0-9+.-]*:\/\/\S+|\bwww\.\S+|(?<![\p{L}\p{N}@._-])[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,24}\b(?![\p{L}\p{N}@-]))/giu;
function everyMatchIn(text, pattern, documentText) {
    for (const match of text.matchAll(pattern)) {
        const said = match[0].replace(/[.,;:!?)]+$/u, "");
        if (!documentText.includes(said))
            return false;
    }
    return true;
}
/**
 * The first problem of `text` as a finding's `field`, or `null` when it may be stored.
 *
 * `callerTurns` is every caller turn of the run's read set (or its index, built once per run); `documentText` is the
 * reviewed version's `config_document` as JSON text.
 */
export function optimisationFindingTextProblemOf(text, callerTurns, documentText, field) {
    if (text.length > MAXIMUM[field])
        return "too_long";
    const index = callerTurns instanceof CopiedCallerWordIndex ? callerTurns : new CopiedCallerWordIndex(callerTurns);
    if (index.copiedIn(text))
        return "copied_caller_words";
    if (!everyMatchIn(text, DIGIT_RUN, documentText))
        return "digit_run";
    if (!everyMatchIn(text, EMAIL_ADDRESS, documentText))
        return "email_address";
    // An email address's domain is also a bare web address: judged once, as the email it is.
    const withoutEmail = text.replace(EMAIL_ADDRESS, " ");
    if (!everyMatchIn(withoutEmail, WEB_ADDRESS, documentText))
        return "web_address";
    return null;
}
//# sourceMappingURL=finding-text.js.map